import Rollbar from 'rollbar';

/**
 * Shared checkIgnore function — filters browser extension errors and common
 * false positives so they don't pollute Rollbar.
 */
function checkIgnore(_isUncaught, args, payload) {
  if (payload && payload.body && payload.body.trace) {
    const frames = payload.body.trace.frames || [];
    for (let i = 0; i < frames.length; i++) {
      const filename = frames[i].filename || '';

      if (
        filename.indexOf('extension://') === 0 ||
        filename.indexOf('chrome-extension://') === 0 ||
        filename.indexOf('moz-extension://') === 0 ||
        filename.indexOf('safari-extension://') === 0
      ) {
        return true;
      }

      if (filename.indexOf('content_script') !== -1 || filename.indexOf('inject') !== -1) {
        return true;
      }
    }
  }

  let message = '';
  if (args && args[0] && typeof args[0].message === 'string') {
    message = args[0].message.toLowerCase();
  } else if (
    payload &&
    payload.body &&
    payload.body.message &&
    typeof payload.body.message.body === 'string'
  ) {
    message = payload.body.message.body.toLowerCase();
  }

  const extensionPatterns = [
    'non-error promise rejection captured',
    'script error',
    'network error',
    'loading chunk',
    'loading css chunk',
    'resizeobserver loop limit exceeded',
  ];

  for (let j = 0; j < extensionPatterns.length; j++) {
    if (message.indexOf(extensionPatterns[j]) !== -1) {
      return true;
    }
  }

  return false;
}

const IGNORED_MESSAGES = [
  'Script error.',
  'Non-Error promise rejection captured',
  /ResizeObserver loop/,
  /Network request failed/,
  /Loading chunk \d+ failed/,
  /ChunkLoadError/,
  /Loading CSS chunk/,
  /TypeError: Failed to fetch/,
  // Chrome 148+ fires a spurious unhandledrejection for intentionally aborted
  // fetches (supersede aborts in effect cleanups) even though the rejection
  // is handled — pure noise, never actionable.
  /signal is aborted without reason/,
];

/**
 * Create a Rollbar configuration object for a client-side app.
 *
 * @param {object} options
 * @param {string} options.accessToken - Rollbar client access token
 * @param {string} options.environment - e.g. 'production', 'staging'
 * @param {string} options.codeVersion - Git commit hash for source map linking
 * @param {string[]} options.hostSafeList - Domains to capture errors from
 */
export function createClientConfig({ accessToken, environment, codeVersion, hostSafeList }) {
  return {
    accessToken,
    // Ensure the instance is accessible as window.Rollbar so call-sites
    // outside React context (tracker helpers, shared packages) can use it.
    globalAlias: 'Rollbar',
    captureUncaught: true,
    captureUnhandledRejections: true,
    itemsPerMinute: 30,
    maxItems: 100,
    hostSafeList,
    // Added to the browser defaults, which lack it. Rollbar keeps the URL it saw at init
    // (e.g. /reset-password?token=…) and reports it as navigation `from` later.
    scrubFields: ['token'],
    ignoredMessages: IGNORED_MESSAGES,
    checkIgnore,
    payload: {
      environment,
      client: {
        javascript: {
          source_map_enabled: true,
          guess_uncaught_frames: true,
          code_version: codeVersion,
        },
      },
    },
  };
}

/**
 * Create a Rollbar instance from a config object.
 * Thin wrapper so apps don't need to import rollbar directly.
 */
export function createRollbarInstance(config) {
  const instance = new Rollbar(config);
  console.log('[Rollbar] Client initialized', { environment: config.payload?.environment });
  return instance;
}

/**
 * Set person context on a Rollbar instance.
 */
export function setRollbarPerson(rollbarInstance, id, email) {
  try {
    rollbarInstance?.configure({
      payload: {
        person: { id, email },
      },
    });
  } catch (error) {
    console.log('ROLLBAR', 'setRollbarPerson', error);
  }
}
